import type * as React from "react";
import { cx } from "../internal/cx.js";

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps extends React.SVGAttributes<SVGSVGElement> {
  /**
   * Accessible label. Default `"Loading"` works for any short-lived
   * spinner; provide a domain-specific label when the spinner replaces
   * meaningful content ("Loading enrollments").
   */
  label?: string;
  size?: SpinnerSize;
}

/**
 * Indeterminate loading indicator. Renders as `<svg role="status">`
 * with a labelled track/indicator. Inline-able next to text. For full
 * page-loading states, prefer the `<DataState>` organism's loading
 * branch (it renders this internally).
 *
 * When the spinner is purely decorative (e.g. nested inside another live
 * region that already announces the loading state), pass `aria-hidden` —
 * the spinner drops the `role="status"` / `<title>` / `aria-label`
 * accessibility affordances so it doesn't double-announce or surface as
 * a duplicate `status` node in tests.
 */
export function Spinner({
  className,
  label = "Loading",
  size = "md",
  "aria-hidden": ariaHidden,
  ...props
}: SpinnerProps) {
  const decorative = ariaHidden === true || ariaHidden === "true";
  return (
    <svg
      aria-hidden={ariaHidden}
      aria-label={decorative ? undefined : label}
      aria-live={decorative ? undefined : "polite"}
      className={cx("cw-v2-spinner", `cw-v2-spinner--${size}`, className)}
      fill="none"
      role={decorative ? undefined : "status"}
      viewBox="0 0 24 24"
      {...props}
    >
      {decorative ? null : <title>{label}</title>}
      <circle
        className="cw-v2-spinner__track"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="cw-v2-spinner__indicator"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        fill="currentColor"
      />
    </svg>
  );
}
